import http from '@/http'

const BASE = '/admin/analytics/checkin'
const REPORTS_BASE = '/admin/reports/checkin'

/**
 * Constrói os params HTTP a partir dos filtros do dashboard.
 * `from` e `to` são Date objects; serializa pra ISO sem timezone shift.
 */
const buildParams = (filters, extra = {}) => {
  const params = { ...extra }
  if (filters.from) params.from = filters.from.toISOString()
  if (filters.to) params.to = filters.to.toISOString()
  if (filters.tagIds && filters.tagIds.length > 0) params.tag_ids = filters.tagIds
  if (filters.operatorId) params.operator_id = filters.operatorId
  return params
}

const unwrap = (response) => response.data

export default {
  overview (filters) {
    return http.get(`${BASE}/overview`, { params: buildParams(filters) }).then(unwrap)
  },

  timeline (filters, bucket) {
    return http.get(`${BASE}/timeline`, { params: buildParams(filters, { bucket }) }).then(unwrap)
  },

  byTag (filters, windowMinutes = null) {
    const extra = windowMinutes ? { window_minutes: windowMinutes } : {}
    return http.get(`${BASE}/by-tag`, { params: buildParams(filters, extra) }).then(unwrap)
  },

  heatmap (filters) {
    return http.get(`${BASE}/heatmap`, { params: buildParams(filters) }).then(unwrap)
  },

  /**
   * primary e secondary são objects { source: 'standard'|'form_field'|'rc_field', id: ... }
   */
  byField (filters, primary, secondary = null) {
    const extra = {
      primary_source: primary.source,
      primary_id: primary.id
    }
    if (secondary && secondary.id) {
      extra.secondary_source = secondary.source
      extra.secondary_id = secondary.id
    }
    return http.get(`${BASE}/by-field`, { params: buildParams(filters, extra) }).then(unwrap)
  },

  operators (filters) {
    return http.get(`${BASE}/operators`, { params: buildParams(filters) }).then(unwrap)
  },

  anomalies (filters) {
    return http.get(`${BASE}/anomalies`, { params: buildParams(filters) }).then(unwrap)
  },

  funnel (filters) {
    return http.get(`${BASE}/funnel`, { params: buildParams(filters) }).then(unwrap)
  },

  insights (filters) {
    return http.get(`${BASE}/insights`, { params: buildParams(filters) }).then(unwrap)
  },

  flow (filters) {
    return http.get(`${BASE}/flow`, { params: buildParams(filters) }).then(unwrap)
  },

  topLocations (filters) {
    return http.get(`${BASE}/top-locations`, { params: buildParams(filters) }).then(unwrap)
  },

  tagDetail (filters, tagId, bucket = '1h') {
    return http.get(`${BASE}/tag/${tagId}`, {
      params: buildParams(filters, { bucket })
    }).then(unwrap)
  },

  operatorDetail (filters, operatorId) {
    return http.get(`${BASE}/operator/${operatorId}`, {
      params: buildParams(filters)
    }).then(unwrap)
  },

  filterOptions () {
    return http.get(`${BASE}/filter-options`).then(unwrap)
  },

  // ── Relatórios exportáveis ────────────────────────────────────────────────

  reportTemplates () {
    return http.get(`${REPORTS_BASE}/templates`).then(unwrap)
  },

  reportPreview (templateKey, payload) {
    return http.post(`${REPORTS_BASE}/${templateKey}/preview`, payload).then(unwrap)
  },

  reportDetails (templateKey, payload) {
    return http.post(`${REPORTS_BASE}/${templateKey}/details`, payload).then(unwrap)
  },

  /**
   * Retorna Blob do arquivo (xlsx ou csv) — caller cria URL e dispara download.
   * Formato é passado via query string pra simplificar download direto.
   */
  reportExport (templateKey, payload, format = 'xlsx') {
    return http.post(`${REPORTS_BASE}/${templateKey}/export?format=${format}`, payload, {
      responseType: 'blob'
    }).then(unwrap)
  },

  // ── Saved Reports ─────────────────────────────────────────────────────────

  reportSavedList () {
    return http.get(`${REPORTS_BASE}/saved`).then(unwrap)
  },

  reportSavedStore (payload) {
    return http.post(`${REPORTS_BASE}/saved`, payload).then(unwrap)
  },

  reportSavedDestroy (id) {
    return http.delete(`${REPORTS_BASE}/saved/${id}`).then(unwrap)
  },

  // ── Schema (builder livre) + custom + drill ──────────────────────────────

  reportSchema () {
    return http.get(`${REPORTS_BASE}/schema`).then(unwrap)
  },

  reportFilterCatalog () {
    return http.get(`${REPORTS_BASE}/filter-catalog`).then(unwrap)
  },

  reportCustomPreview (spec) {
    return http.post(`${REPORTS_BASE}/custom/preview`, spec).then(unwrap)
  },

  reportCustomExport (spec, format = 'xlsx') {
    return http.post(`${REPORTS_BASE}/custom/export`, { ...spec, format }, {
      responseType: 'blob'
    }).then(unwrap)
  },

  reportDrill (payload) {
    return http.post(`${REPORTS_BASE}/drill`, payload).then(unwrap)
  }
}
